import React, { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowRight, ScanLine } from "lucide-react";
import { useLang, IMAGES } from "../i18n";
import { MaskLine } from "./Motion";

export const Hero = () => {
  const { t } = useLang();
  const h = t.hero;
  const slides = h.slides || [];
  const [active, setActive] = useState(0);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -50]);

  useEffect(() => {
    if (!slides.length) return;
    const id = setInterval(() => setActive((a) => (a + 1) % slides.length), 3200);
    return () => clearInterval(id);
  }, [slides.length]);

  const slide = slides[active] || {};

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen w-full overflow-hidden">
      {/* soft ambient washes */}
      <div className="absolute top-24 -left-32 w-[520px] h-[520px] rounded-full bg-[var(--green)]/8 blur-[150px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[560px] h-[560px] rounded-full bg-[var(--gold)]/8 blur-[160px] pointer-events-none" />

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-32 md:pt-36 pb-16 grid lg:grid-cols-12 gap-12 items-center min-h-screen">
        {/* Left: text */}
        <motion.div style={{ y: yText }} className="lg:col-span-6 relative z-10">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="inline-flex items-center gap-3 mb-8 rounded-full border border-[var(--border)] bg-white/70 backdrop-blur-sm pl-3 pr-4 py-1.5 w-fit"
          >
            <ScanLine className="w-3.5 h-3.5 text-[var(--gold)]" />
            <span className="overline text-[10px] text-[var(--text-primary)]">{h.badge}</span>
          </motion.div>

          <h1 className="font-cormorant font-light leading-[1.02] tracking-tight text-[13vw] sm:text-[9vw] lg:text-[5.6vw]">
            <MaskLine delay={0.35} className="pb-[0.12em]">{h.line1}</MaskLine>
            <MaskLine delay={0.45} className="pb-[0.12em]">{h.line2}</MaskLine>
            <MaskLine delay={0.55} className="pb-[0.12em]">
              <span className="italic text-[var(--gold)]">{h.line3}</span>{" "}
              <span className="text-[var(--text-secondary)]">{h.line3accent}</span>
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="font-outfit font-light text-base md:text-lg text-[var(--text-secondary)] max-w-xl mt-8 leading-relaxed"
          >
            {h.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="flex flex-wrap items-center gap-4 mt-10"
          >
            <a
              href="#patent"
              data-testid="hero-cta-patent"
              className="group inline-flex items-center gap-2 bg-[var(--gold)] text-white font-outfit font-medium text-sm rounded-full pl-6 pr-5 py-3.5 hover:gap-3 transition-all duration-300"
            >
              {h.cta}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
            </a>
            <a
              href="#services"
              data-testid="hero-cta-services"
              className="inline-flex items-center gap-2 border border-[var(--border)] text-[var(--text-primary)] font-outfit text-sm rounded-full px-6 py-3.5 hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors duration-300"
            >
              {h.cta2}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 1 }}
            className="flex gap-10 mt-14"
          >
            {[[h.stat1, h.stat1l], [h.stat2, h.stat2l], [h.stat3, h.stat3l]].map(([v, l], i) => (
              <div key={i}>
                <div className="font-cormorant text-4xl text-[var(--text-primary)]">{v}</div>
                <div className="overline text-[9px] text-[var(--text-secondary)] mt-1">{l}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* Right: patent visual card */}
        <motion.div style={{ y: yImg }} className="lg:col-span-6 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <motion.div
              animate={{ y: [-10, 10, -10] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
              className="relative rounded-[2rem] overflow-hidden border border-[var(--border)] shadow-[0_30px_80px_-30px_rgba(30,43,35,0.35)]"
              data-testid="hero-carousel"
            >
              <div className="relative w-full h-[440px] lg:h-[600px]">
                <AnimatePresence mode="popLayout">
                  <motion.img
                    key={active}
                    src={IMAGES[slide.img]}
                    alt={slide.t}
                    data-testid="hero-image"
                    initial={{ opacity: 0, scale: 1.06 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </AnimatePresence>
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent pointer-events-none" />
              </div>

              <motion.div
                animate={{ top: ["8%", "88%", "8%"] }}
                transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
                className="absolute left-5 right-5 h-px bg-[var(--green)]/70 shadow-[0_0_20px_2px_rgba(60,122,78,0.6)]"
              />
              {["top-5 left-5 border-t border-l", "top-5 right-5 border-t border-r", "bottom-5 left-5 border-b border-l", "bottom-5 right-5 border-b border-r"].map((c, i) => (
                <span key={i} className={`absolute ${c} w-6 h-6 border-white/70`} />
              ))}

              {/* slide number + label */}
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={active}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.4 }}
                    data-testid="hero-slide-label"
                    className="flex items-center gap-3"
                  >
                    <span className="font-cormorant text-4xl text-white leading-none">{slide.n}</span>
                    <span className="font-outfit text-sm text-white/90 max-w-[10rem] leading-tight">{slide.t}</span>
                  </motion.div>
                </AnimatePresence>
                <div className="flex gap-1.5">
                  {slides.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setActive(i)}
                      data-testid={`hero-dot-${i}`}
                      className={`h-1.5 rounded-full transition-all duration-300 ${i === active ? "w-6 bg-white" : "w-2 bg-white/50"}`}
                      aria-label={`slide ${i + 1}`}
                    />
                  ))}
                </div>
              </div>
            </motion.div>

            <motion.div
              animate={{ y: [10, -10, 10] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -bottom-5 -left-5 glass rounded-xl px-4 py-3 shadow-lg"
            >
              <div className="font-jetbrains text-[10px] text-[var(--green)] tracking-widest">● LIVE</div>
              <div className="font-jetbrains text-[11px] text-[var(--text-secondary)] mt-1">ID: A1B2C3D4E5F6</div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-6 left-0 right-0 z-10 px-6 md:px-10"
      >
        <div className="max-w-[1400px] mx-auto flex items-center justify-between border-t border-[var(--border)] pt-4">
          <span className="overline text-[10px] text-[var(--text-secondary)] truncate">{h.tagline}</span>
          <span className="overline text-[10px] text-[var(--text-secondary)] shrink-0 ml-4">↓ scroll</span>
        </div>
      </motion.div>
    </section>
  );
};
